import React, { useMemo, useRef, useState } from "react";
import { shotLabel } from "../utils/score";
import { Download, Maximize2, RotateCcw, Target, ZoomIn, ZoomOut } from "lucide-react";
import { Shot } from "../data/types";
import { formatLength, lengthLabel, formatVelocity, useUnits } from "../utils/units";

/*
 * Interactive target rendered as the Global Range Solutions scorecard: dark
 * board, gold rules, cream target face, with the shooter information, running
 * shot list, sighter tally and score boxes printed in the corners like the
 * physical card.
 *
 * The rings are NOT fixed. Every face has its own geometry — a 600y face has
 * larger rings than a 300y one, and F-class adds an X inside the centre — so
 * they are drawn from the target definition the string was shot on, which the
 * import snapshots onto the record. Drawing one hardcoded face made shots
 * appear in rings they were not scored in.
 *
 * Without a definition no rings are drawn at all: an approximate target is
 * worse than none, because it invites exactly that misreading.
 */

/** One scoring zone of a target face, as published by the target server. */
export interface FaceRing {
  label: string;
  points: number;
  isCentre: boolean;
  shape: "ring" | "rect";
  radiusMoa: number;
  widthMm?: number;
  heightMm?: number;
  colour?: string;
  borderColour?: string | null;
}

export interface TargetFace {
  name: string;
  distanceM?: number;
  mmPerMoa?: number;
  classTag?: string | null;
  rings: FaceRing[];
}

const SIZE = 640;
const C = SIZE / 2;
/* The face is scaled to fill this radius, whatever its rings measure in MOA. */
const FACE_RADIUS_PX = 208;

/*
 * Black and white, like the paper.
 *
 * A fullbore target is white with a black aiming mark, and the earlier beige
 * face with gold rules read as decoration rather than as the thing being shot
 * at. Ring lines follow the same rule as the printed face: black on the white
 * paper, white where they cross the black aiming mark, so every ring stays
 * visible without inventing a colour that is not on a real target.
 *
 * Shot markers stay red. They are marks made ON the target, not part of it, and
 * they have to be findable at a glance.
 */
const INK = "#101114"; // the board the face is mounted on
const RULE = "#FFFFFF"; // frame and headings
const RULE_DIM = "#6b7280"; // secondary rules and labels
const FACE = "#FFFFFF"; // the paper
const FACE_LINE = "#111111"; // ring lines on the white paper
const FACE_TEXT = "#111111"; // ring numbers on the white paper
const AIMING_MARK = "#111111"; // the black centre of the face
const ON_BLACK = "#FFFFFF"; // rings and numbers inside the aiming mark

/** Everything the card prints besides the shots themselves. */
export interface TargetCardInfo {
  sid: string;
  shooter: string;
  rifleClass?: string;
  competition?: string;
  match?: string;
  stringNo?: number;
  totalScore: number;
  vCount: number;
  centreLabel?: string;
  /** Sighters allowed at this range (the card prints one bubble each). */
  sightersAllowed?: number;
  /** Face name from the shot log, e.g. "AU-600y". */
  faceName?: string;
}

const fit = (s: string | undefined, max: number) => {
  const t = s ?? "";
  return t.length > max ? t.slice(0, max - 1) + "…" : t;
};

/** A thin gold-ruled corner panel with a small-caps title. */
function Panel({ x, y, w, h, title, children }: { x: number; y: number; w: number; h: number; title: string; children?: React.ReactNode }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={6} fill={INK} stroke={RULE_DIM} strokeWidth="1" opacity={0.96} />
      <text x={x + 10} y={y + 15} fontSize="8.5" fontWeight="700" letterSpacing="1.5" fill={RULE}>
        {title.toUpperCase()}
      </text>
      <line x1={x + 10} y1={y + 20} x2={x + w - 10} y2={y + 20} stroke={RULE_DIM} strokeWidth="0.75" />
      {children}
    </g>
  );
}

export default function TargetMap({
  shots,
  selectedShot,
  onSelectShot,
  velocityUnit = "fps",
  info,
  face,
}: {
  shots: Shot[];
  selectedShot: number | null;
  onSelectShot: (n: number | null) => void;
  velocityUnit?: string;
  info?: TargetCardInfo;
  /** The face this string was shot on. Without it the rings are not drawn. */
  face?: TargetFace | null;
}) {
  const { units } = useUnits();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showGroupCentre, setShowGroupCentre] = useState(true);
  // On by default — it was there before and is useful for reading the spread —
  // but it moves with the group, which is puzzling until you know what it is.
  const [showHitArea, setShowHitArea] = useState(true);
  const [showGrid, setShowGrid] = useState(false);
  // Small by default — at 1x the markers hid each other and the group was unreadable.
  const [shotSize, setShotSize] = useState(0.6);
  const [hover, setHover] = useState<Shot | null>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const scoring = shots.filter((s) => !s.sighter);
  const sighters = shots.length - scoring.length;
  const centre = useMemo(() => {
    const cx = scoring.reduce((a, s) => a + s.x, 0) / (scoring.length || 1);
    const cy = scoring.reduce((a, s) => a + s.y, 0) / (scoring.length || 1);
    return { x: cx, y: cy };
  }, [scoring]);

  const hitArea = useMemo(() => {
    // An all-sighter string leaves nothing to bound; Math.min of [] is Infinity.
    if (!scoring.length) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    const xs = scoring.map((s) => s.x);
    const ys = scoring.map((s) => s.y);
    return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
  }, [scoring]);

  /*
   * Scale is derived per face rather than fixed, because ring sizes in MOA
   * differ from face to face. The extent is the widest scoring ring, or the
   * furthest shot when someone has gone outside the face — a shot off the paper
   * still has to be visible.
   */
  const rings = useMemo(
    () => (face?.rings ?? []).filter((r) => r.shape === "ring" && r.radiusMoa > 0).sort((a, b) => b.radiusMoa - a.radiusMoa),
    [face]
  );
  const pxPerMoa = useMemo(() => {
    const widestRing = rings.length ? rings[0].radiusMoa : 0;
    const furthestShot = shots.reduce((m, s) => Math.max(m, Math.hypot(s.x, s.y)), 0);
    const extent = Math.max(widestRing, furthestShot * 1.15, 1);
    return FACE_RADIUS_PX / extent;
  }, [rings, shots]);

  /*
   * The aiming mark is the ring the definition paints black — the black patch a
   * shooter actually aims at. Everything inside it is drawn in white, because
   * black lines on black paper cannot be seen. 0 when the face has no black
   * zone, in which case the whole face is treated as white paper.
   */
  const aimingMarkMoa = useMemo(
    () => rings.find((r) => (r.colour || "").toLowerCase() === "black")?.radiusMoa ?? 0,
    [rings]
  );

  const toPx = (moaX: number, moaY: number) => ({
    x: C + moaX * pxPerMoa,
    y: C - moaY * pxPerMoa, // positive Y (MOA) is up
  });

  const view = SIZE / zoom;
  const viewBox = `${C - view / 2 + pan.x} ${C - view / 2 + pan.y} ${view} ${view}`;

  const reset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    onSelectShot(null);
  };

  const download = () => {
    if (!svgRef.current) return;
    const blob = new Blob([new XMLSerializer().serializeToString(svgRef.current)], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "shot-map.svg";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const fullscreen = () => wrapRef.current?.requestFullscreen?.();

  const onPointerDown = (e: React.PointerEvent) => {
    dragRef.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current || zoom === 1) return;
    const dx = ((e.clientX - dragRef.current.x) / (wrapRef.current?.clientWidth || SIZE)) * view;
    const dy = ((e.clientY - dragRef.current.y) / (wrapRef.current?.clientWidth || SIZE)) * view;
    setPan((p) => ({ x: p.x - dx, y: p.y - dy }));
    dragRef.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = () => (dragRef.current = null);

  const toolBtn = (bg: string) =>
    `flex h-9 w-9 items-center justify-center rounded-lg ${bg} text-white shadow-sm transition-transform hover:scale-105 active:scale-95`;

  const groupW = (hitArea.maxX - hitArea.minX).toFixed(1);
  const groupH = (hitArea.maxY - hitArea.minY).toFixed(1);

  /*
   * Ring numbers sit at the midpoint of each band, printed on all four axes as
   * on the physical card. A band runs from its own edge in to the next ring, so
   * the innermost value is not labelled here — it goes in the centre.
   */
  const bandMid = useMemo(
    () =>
      rings
        .map((ring, i) => ({ label: ring.label, r: (ring.radiusMoa + (rings[i + 1]?.radiusMoa ?? 0)) / 2 }))
        .filter((b, i) => i < rings.length - 1),
    [rings]
  );

  const centreLabel = info?.centreLabel ?? "V";
  const sighterBubbles = Math.max(info?.sightersAllowed ?? 2, Math.min(sighters, 6));
  const shotRows = shots.slice(0, 12);

  return (
    <div>
      {/* Controls row */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-ink">
          <input type="checkbox" checked={showGroupCentre} onChange={(e) => setShowGroupCentre(e.target.checked)} className="h-4 w-4 accent-primary" />
          Group Centre
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-ink">
          <input type="checkbox" checked={showHitArea} onChange={(e) => setShowHitArea(e.target.checked)} className="h-4 w-4 accent-primary" />
          Hit Area
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-ink">
          <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} className="h-4 w-4 accent-primary" />
          Show Grid (1 MOA)
        </label>
      </div>

      <div ref={wrapRef} className="relative overflow-hidden rounded-xl border border-line" style={{ background: INK }}>
        {/* Floating toolbar */}
        <div className="absolute right-3 top-3 z-10 flex gap-1.5">
          {/*
            Up to eight times. Four barely separated a tight group on a 600-yard
            face — and a tight group is the one a shooter most wants to look at.
          */}
          <button className={toolBtn("bg-primary")} onClick={() => setZoom((z) => Math.min(8, z + 0.5))} aria-label="Zoom in"><ZoomIn size={16} /></button>
          <button className={toolBtn("bg-primary")} onClick={() => setZoom((z) => Math.max(1, z - 0.5))} aria-label="Zoom out"><ZoomOut size={16} /></button>
          <button className={toolBtn("bg-success")} onClick={() => setShowGroupCentre((v) => !v)} aria-label="Toggle group centre"><Target size={16} /></button>
          <button className={toolBtn("bg-orange-500")} onClick={reset} aria-label="Reset view"><RotateCcw size={16} /></button>
          <button className={toolBtn("bg-primary")} onClick={download} aria-label="Download shot map"><Download size={16} /></button>
          <button className={toolBtn("bg-primary")} onClick={fullscreen} aria-label="Fullscreen"><Maximize2 size={16} /></button>
        </div>

        {/* Shot size slider */}
        <div className="absolute right-3 top-14 z-10 flex items-center gap-2 rounded-lg bg-white/95 px-3 py-2 shadow-card">
          <span className="text-xs font-semibold text-ink">Shot Size</span>
          <input type="range" min={0.3} max={1.5} step={0.15} value={shotSize} onChange={(e) => setShotSize(Number(e.target.value))} className="w-24 accent-primary" />
          <span className="w-8 text-right text-xs font-bold text-ink">{shotSize.toFixed(2)}x</span>
        </div>

        {/*
          Bounded to the viewport so the card still fits a laptop screen, but
          generously: the statistics used to sit beside the target and had to be
          kept in view, and they are now below it. The board is square, so
          capping the height caps the width with it; `mx-auto` keeps it centred
          once it stops filling the column.
        */}
        <svg
          ref={svgRef}
          viewBox={viewBox}
          // Inline rather than a utility class: an arbitrary Tailwind value
          // containing min() with a comma is not reliably generated.
          style={{ maxHeight: "min(78vh, 900px)" }}
          className={`mx-auto block h-auto w-full ${zoom > 1 ? "cursor-grab active:cursor-grabbing" : ""}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          {/* Board + double gold frame */}
          <rect x="0" y="0" width={SIZE} height={SIZE} fill={INK} />
          <rect x="6" y="6" width={SIZE - 12} height={SIZE - 12} rx="10" fill="none" stroke={RULE_DIM} strokeWidth="1" />
          <rect x="10" y="10" width={SIZE - 20} height={SIZE - 20} rx="8" fill="none" stroke={RULE} strokeWidth="0.5" opacity="0.6" />

          {/* Masthead */}
          <text x={C} y={30} textAnchor="middle" fontSize="13" fontWeight="700" letterSpacing="5" fill={FACE}>
            GLOBAL RANGE
          </text>
          <text x={C} y={43} textAnchor="middle" fontSize="8" fontWeight="600" letterSpacing="6" fill={RULE}>
            S O L U T I O N S
          </text>


          {/*
            The face, drawn from its own definition — widest ring first so each
            smaller one lands on top.

            White paper, with the aiming mark filled black exactly where the
            definition says it is. A ring line is black on the paper and white
            once it is inside the aiming mark, which is how it is printed and the
            only way every ring stays visible against its own background.
          */}
          {/*
            The paper first — the aiming mark often IS the outermost scoring
            ring (on the 400y face the black reaches the 2 ring), so the white
            cannot be the outer ring's own fill or the black would be painted
            over. The disc sits slightly proud of the widest ring, as the card
            does around the printed face.
          */}
          {rings.length > 0 && (
            <circle cx={C} cy={C} r={rings[0].radiusMoa * pxPerMoa * 1.1} fill={FACE} stroke={FACE_LINE} strokeWidth="1" />
          )}
          {rings.map((ring) => {
            const isAimingMark = ring.radiusMoa === aimingMarkMoa;
            const inBlack = aimingMarkMoa > 0 && ring.radiusMoa <= aimingMarkMoa;
            return (
              <circle
                key={`${ring.label}-${ring.radiusMoa}`}
                cx={C}
                cy={C}
                r={ring.radiusMoa * pxPerMoa}
                fill={isAimingMark ? AIMING_MARK : "none"}
                stroke={inBlack && !isAimingMark ? ON_BLACK : FACE_LINE}
                strokeWidth="1"
              />
            );
          })}

          {/* Fine crosshair, following the same black-on-white rule */}
          {rings.length > 0 && (
            <g strokeWidth="0.5" opacity="0.45">
              <line x1={C - rings[0].radiusMoa * pxPerMoa} y1={C} x2={C + rings[0].radiusMoa * pxPerMoa} y2={C} stroke={FACE_LINE} />
              <line x1={C} y1={C - rings[0].radiusMoa * pxPerMoa} x2={C} y2={C + rings[0].radiusMoa * pxPerMoa} stroke={FACE_LINE} />
              {aimingMarkMoa > 0 && (
                <>
                  <line x1={C - aimingMarkMoa * pxPerMoa} y1={C} x2={C + aimingMarkMoa * pxPerMoa} y2={C} stroke={ON_BLACK} />
                  <line x1={C} y1={C - aimingMarkMoa * pxPerMoa} x2={C} y2={C + aimingMarkMoa * pxPerMoa} stroke={ON_BLACK} />
                </>
              )}
            </g>
          )}

          {/* Ring values on all four axes, like the printed face */}
          {bandMid.map(({ label, r }) => (
            <g
              key={label}
              fontSize="12"
              fontWeight="700"
              fill={aimingMarkMoa > 0 && r <= aimingMarkMoa ? ON_BLACK : FACE_TEXT}
              textAnchor="middle"
            >
              <text x={C - r * pxPerMoa} y={C + 4.5}>{label}</text>
              <text x={C + r * pxPerMoa} y={C + 4.5}>{label}</text>
              <text x={C} y={C - r * pxPerMoa + 4.5}>{label}</text>
              <text x={C} y={C + r * pxPerMoa + 4.5}>{label}</text>
            </g>
          ))}
          {rings.length > 0 && (
            <text
              x={C}
              y={C + 5}
              textAnchor="middle"
              fontSize="14"
              fontWeight="800"
              fill={aimingMarkMoa > 0 ? ON_BLACK : FACE_TEXT}
            >
              {rings[rings.length - 1].label || centreLabel}
            </text>
          )}

          {/*
            No definition for this face: the shots are still plotted true to
            their MOA coordinates, but nothing is drawn that could be mistaken
            for a scoring ring.
          */}
          {rings.length === 0 && (
            <g>
              <circle cx={C} cy={C} r={FACE_RADIUS_PX} fill="none" stroke={RULE_DIM} strokeWidth="1" strokeDasharray="4 6" opacity="0.5" />
              <text x={C} y={C - FACE_RADIUS_PX - 12} textAnchor="middle" fontSize="9" fill={RULE_DIM} letterSpacing="1">
                NO TARGET DEFINITION FOR {(info?.faceName || "THIS FACE").toUpperCase()} — RINGS NOT DRAWN
              </text>
            </g>
          )}

          {/*
            The 1 MOA grid, drawn OVER the face.

            SVG paints in document order, so drawing it first put it behind the
            paper — a grid that stops at the edge of the target measures nothing,
            because every shot is inside that edge. Mid-grey at low opacity reads
            on the white paper and on the black aiming mark alike, and leaves the
            shots the darkest thing on the target.
          */}
          {showGrid && (
            <g stroke="#7c8699" strokeWidth="0.6" opacity="0.55">
              {Array.from({ length: 41 }, (_, i) => i - 20).map((m) => (
                <g key={m}>
                  <line x1={C + m * pxPerMoa} y1={0} x2={C + m * pxPerMoa} y2={SIZE} />
                  <line x1={0} y1={C + m * pxPerMoa} x2={SIZE} y2={C + m * pxPerMoa} />
                </g>
              ))}
            </g>
          )}

          {/*
            Hit area — the box enclosing the scoring shots, so it sits wherever
            the group landed rather than around the centre. Useful for reading
            the spread, distracting when you only want to see the shots, so it
            can be switched off. Drawn in the group-centre green rather than
            white: on a white face a white dashed box is invisible.
          */}
          {showHitArea &&
            scoring.length > 1 &&
            (() => {
              const a = toPx(hitArea.minX, hitArea.maxY);
              const b = toPx(hitArea.maxX, hitArea.minY);
              return (
                <rect
                  x={a.x}
                  y={a.y}
                  width={b.x - a.x}
                  height={b.y - a.y}
                  fill="none"
                  stroke="#16A34A"
                  strokeWidth="1.25"
                  strokeDasharray="5 4"
                  opacity="0.85"
                />
              );
            })()}

          {/* Group centre */}
          {showGroupCentre &&
            (() => {
              const p = toPx(centre.x, centre.y);
              return (
                <g stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round">
                  <line x1={p.x - 10} y1={p.y} x2={p.x + 10} y2={p.y} />
                  <line x1={p.x} y1={p.y - 10} x2={p.x} y2={p.y + 10} />
                </g>
              );
            })()}

          {/* Shots */}
          {shots.map((s) => {
            const p = toPx(s.x, s.y);
            const r = 12 * shotSize;
            const isSel = selectedShot === s.n;
            const isHover = hover?.n === s.n;
            return (
              <g
                key={s.n}
                className="cursor-pointer"
                onMouseEnter={() => setHover(s)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onSelectShot(isSel ? null : s.n)}
              >
                {(isSel || isHover) && <circle cx={p.x} cy={p.y} r={r + 5} fill="rgba(220,38,38,0.3)" />}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={r}
                  fill={s.sighter ? FACE : "#C0281E"}
                  stroke={s.sighter ? "#C0281E" : FACE}
                  strokeWidth="1.75"
                  style={{ transition: "r 120ms" }}
                />
                {shotSize >= 0.55 && (
                  <text x={p.x} y={p.y + 4 * shotSize} textAnchor="middle" fontSize={11 * shotSize} fontWeight="700" fill={s.sighter ? "#C0281E" : FACE}>
                    {shotLabel(s)}
                  </text>
                )}
              </g>
            );
          })}

          {/* ---- Printed corner panels (the scorecard chrome) ---- */}
          {info && (
            <>
              <Panel x={18} y={52} w={168} h={110} title="Shooter Information">
                {[
                  ["S.I.D.", info.sid],
                  ["Name", fit(info.shooter, 20)],
                  ["Class", info.rifleClass ?? "—"],
                  ["Event", fit(info.competition, 18)],
                  ["Match", fit(info.match, 18)],
                  ["String", info.stringNo != null ? String(info.stringNo) : "—"],
                ].map(([l, v], i) => (
                  <g key={l} fontSize="8.5">
                    <text x={30} y={66 + 20 + i * 13} fill={RULE} letterSpacing="0.5">{String(l).toUpperCase()}</text>
                    <text x={176} y={66 + 20 + i * 13} fill={FACE} textAnchor="end" fontWeight="600">{v}</text>
                  </g>
                ))}
              </Panel>

              <Panel x={SIZE - 18 - 128} y={52} w={128} h={Math.min(shotRows.length, 12) * 12 + 44} title="Shots · Score">
                {shotRows.map((s, i) => (
                  <g key={s.n} fontSize="8.5">
                    <text x={SIZE - 18 - 128 + 12} y={52 + 32 + i * 12} fill={s.sighter ? RULE_DIM : RULE}>
                      {shotLabel(s)}
                    </text>
                    <text x={SIZE - 18 - 12} y={52 + 32 + i * 12} fill={s.sighter ? RULE_DIM : FACE} textAnchor="end" fontWeight="700">
                      {String(s.score)}
                    </text>
                  </g>
                ))}
                <line x1={SIZE - 18 - 128 + 10} y1={52 + 34 + Math.min(shotRows.length, 12) * 12 - 8} x2={SIZE - 18 - 10} y2={52 + 34 + Math.min(shotRows.length, 12) * 12 - 8} stroke={RULE_DIM} strokeWidth="0.75" />
                <text x={SIZE - 18 - 128 + 12} y={52 + 34 + Math.min(shotRows.length, 12) * 12 + 3} fontSize="8.5" fill={RULE} letterSpacing="1">TOTAL</text>
                <text x={SIZE - 18 - 12} y={52 + 34 + Math.min(shotRows.length, 12) * 12 + 3} fontSize="9" fill={FACE} textAnchor="end" fontWeight="800">
                  {info.totalScore}.{info.vCount}{centreLabel}
                </text>
              </Panel>

              <Panel x={18} y={SIZE - 52 - 58} w={168} h={58} title="Sighters">
                {Array.from({ length: sighterBubbles }, (_, i) => (
                  <circle
                    key={i}
                    cx={34 + i * 22}
                    cy={SIZE - 52 - 58 + 40}
                    r={6.5}
                    fill={i < sighters ? FACE : "none"}
                    stroke={RULE}
                    strokeWidth="1"
                  />
                ))}
                <text x={34 + sighterBubbles * 22 + 4} y={SIZE - 52 - 58 + 43.5} fontSize="8.5" fill={RULE_DIM}>
                  {sighters} fired
                </text>
              </Panel>

              <Panel x={SIZE - 18 - 128} y={SIZE - 52 - 82} w={128} h={82} title={`${centreLabel} Count`}>
                <text x={SIZE - 18 - 64} y={SIZE - 52 - 82 + 38} textAnchor="middle" fontSize="16" fontWeight="800" fill={FACE}>
                  {info.vCount}
                </text>
                <line x1={SIZE - 18 - 128 + 10} y1={SIZE - 52 - 82 + 46} x2={SIZE - 18 - 10} y2={SIZE - 52 - 82 + 46} stroke={RULE_DIM} strokeWidth="0.75" />
                <text x={SIZE - 18 - 128 + 10} y={SIZE - 52 - 82 + 59} fontSize="8.5" fontWeight="700" letterSpacing="1.5" fill={RULE}>
                  TOTAL SCORE
                </text>
                <text x={SIZE - 18 - 64} y={SIZE - 52 - 82 + 76} textAnchor="middle" fontSize="15" fontWeight="800" fill={RULE}>
                  {info.totalScore}
                </text>
              </Panel>
            </>
          )}

          {/* Foot line */}
          <text x={C} y={SIZE - 20} textAnchor="middle" fontSize="8" letterSpacing="4" fill={RULE_DIM}>
            PRECISION · FOCUS · EXCELLENCE
          </text>
        </svg>

        {/* Tooltip */}
        {hover && (
          <div className="pointer-events-none absolute left-3 bottom-3 z-10 rounded-lg bg-navy-950 px-3.5 py-2.5 text-white shadow-modal">
            <div className="text-[13px] font-bold">
              Shot {shotLabel(hover)} — {hover.score}
            </div>
            <div className="mt-0.5 text-xs text-slate-300">
              X {formatLength(hover.x, units.length, face?.mmPerMoa)} {lengthLabel(units.length, face?.mmPerMoa)} · Y{" "}
              {formatLength(hover.y, units.length, face?.mmPerMoa)} {lengthLabel(units.length, face?.mmPerMoa)} ·{" "}
              {formatVelocity(hover.velocity, units.velocity, true)} · {hover.time}
            </div>
          </div>
        )}
      </div>

      {/* Legend — the ring sizes actually drawn, not a fixed list */}
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-ink">
        {face?.name ? (
          <span className="flex items-center gap-1.5 font-bold text-primary">
            {face.name}
            {face.distanceM ? <span className="font-medium text-muted">({Math.round(face.distanceM)} m)</span> : null}
          </span>
        ) : null}
        {rings.map((r) => (
          <span key={`${r.label}-${r.radiusMoa}`} className="flex items-center gap-1.5 font-medium">
            {/* The legend sits on the page, not the target, so its rule is dark. */}
            <span className="inline-block h-0.5 w-5 rounded" style={{ background: FACE_LINE }} />
            {r.label} ({formatLength(r.radiusMoa, units.length, face?.mmPerMoa)} {lengthLabel(units.length, face?.mmPerMoa)})
          </span>
        ))}
        <span className="flex items-center gap-1.5 font-medium">
          <span className="h-3 w-3 rounded-full" style={{ background: "#C0281E" }} /> Scoring ({scoring.length})
        </span>
        <span className="flex items-center gap-1.5 font-medium">
          <span className="h-3 w-3 rounded-full border-2" style={{ borderColor: "#C0281E", background: FACE }} /> Sighter ({sighters})
        </span>
        {showGroupCentre && <span className="flex items-center gap-1.5 font-medium text-success">＋ Group Centre</span>}
        {showHitArea && scoring.length > 1 && (
          <span className="flex items-center gap-1.5 font-medium text-success">
            ▢ Hit Area ({formatLength(Number(groupW), units.length, face?.mmPerMoa)} × {formatLength(Number(groupH), units.length, face?.mmPerMoa)}{" "}
            {lengthLabel(units.length, face?.mmPerMoa)}) — the box around your group
          </span>
        )}
      </div>
    </div>
  );
}
